// press.js — Drücken mit Masse (Bewegung Runde 3): EIN delegierter Zeiger-Zuhörer gibt Knöpfen, Chips, klickbaren
// Karten und Listenzeilen unter dem Finger etwas nach (Effekt-Feder) und lässt sie beim Loslassen mit leichtem
// Überschwingen zurückfedern (spatial-fast). Gesetzt wird nur die scale-Eigenschaft — translate/transform anderer
// Bewegungen bleiben unberührt. Ein langer Druck hält die Form, bis losgelassen wird. Reduzierte Bewegung: nichts.
import { reducedMotion } from './dom.js';
import { spring, haptic } from '../motion/physics.js';
import { play } from '../motion/motion.js';

export const PRESS_SEL = '.vc-btn, .vc-icon-btn, .vc-fab, .vc-chip, .vc-card.is-click, a.vc-li, button.vc-li, .vc-li.is-click, .vc-fab-menu-item';

/** Wie weit ein Element einsinkt: höchstens 4 %, große Flächen höchstens 6 px — eine breite Karte soll nicht kippen. */
export function pressScale(width, height) {
  const side = Math.max(1, Math.max(width, height));
  return 1 - Math.min(0.04, 6 / side);
}

let installed = false;
export function installPress(root = document) {
  if (installed) return;
  installed = true;
  const active = new WeakMap();
  // Eine Feder je Bewegung; die neue startet beim aktuellen Stand der alten (Unterbrechung mitten im Federn)
  const go = (el, target, kind) => {
    const prev = active.get(el);
    const from = prev ? prev.value : (parseFloat(el.style.scale) || 1);
    if (prev) prev.stop();
    const s = spring(from, { spring: kind, precision: 0.0005, onUpdate: (x) => { el.style.scale = Math.abs(x - 1) < 0.0005 ? '' : String(x); } });
    active.set(el, s);
    s.to(target).then(() => { if (active.get(el) === s && target === 1) active.delete(el); });
  };
  let held = null;
  const release = () => { if (!held) return; const el = held; held = null; go(el, 1, 'spatial-fast'); };
  root.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || reducedMotion()) return;
    const el = e.target.closest && e.target.closest(PRESS_SEL);
    if (!el || el.disabled || el.getAttribute('aria-disabled') === 'true' || el.dataset.noPress != null) return;
    release();
    held = el;
    const r = el.getBoundingClientRect();
    go(el, pressScale(r.width, r.height), 'effects-fast');
  }, { passive: true });
  for (const ev of ['pointerup', 'pointercancel', 'dragstart']) root.addEventListener(ev, release, { passive: true });
  // Häkchen und Schalter: das Kästchen federt beim Umschalten aus 0,7 auf (spatial-fast überschwingt), dazu ein Tick
  root.addEventListener('change', (e) => {
    const el = e.target;
    if (!el || el.tagName !== 'INPUT' || (el.type !== 'checkbox' && el.type !== 'radio')) return;
    haptic('tick');
    if (el.classList.contains('vc-switch')) return; // der Griff federt per CSS
    play(el, [{ scale: '0.7' }, { scale: '1' }], { spring: 'spatial-fast' });
  });
  // Finger gleitet vom Element weg: loslassen (nur das gedrückte Element selbst, nicht seine Kinder)
  root.addEventListener('pointerleave', (e) => { if (e.target === held) release(); }, { passive: true, capture: true });
}

/** Richtung des Scrollens mit Schwelle: liefert 'down' | 'up' | null, sobald sich über threshold px angesammelt haben. */
export function scrollDirection(state, top, threshold = 24) {
  const d = top - (state.last ?? top);
  state.last = top;
  if (!d) return null;
  state.acc = Math.sign(d) === Math.sign(state.acc || 0) ? state.acc + d : d;
  if (Math.abs(state.acc) < threshold) return null;
  const dir = state.acc > 0 ? 'down' : 'up';
  state.acc = 0;
  return dir;
}

let fabInstalled = false;
/** Erweiterte FABs klappen beim Herunterscrollen zum Symbol zusammen und kommen beim Hochscrollen zurück (jeder Scroller). */
export function installFabCollapse(root = document) {
  if (fabInstalled) return;
  fabInstalled = true;
  const states = new WeakMap();
  root.addEventListener('scroll', (e) => {
    const sc = e.target === document ? (document.scrollingElement || document.documentElement) : e.target;
    if (!sc || typeof sc.scrollTop !== 'number') return;
    let stt = states.get(sc);
    if (!stt) { stt = {}; states.set(sc, stt); }
    const dir = scrollDirection(stt, sc.scrollTop);
    if (!dir) return;
    for (const f of document.querySelectorAll('.vc-fab.is-extended')) f.classList.toggle('is-collapsed', dir === 'down' && sc.scrollTop > 24);
  }, { passive: true, capture: true });
}
